<template>
	<SidePanelContent class="advisedActionsPanel w-[30rem]" hide-on-click-outside>
		<template #heading>{{ t('actions.panel.title') }}</template>

		<div class="font-body-s mb-05 mt-03 text-ink-87">
			<div v-if="isLoading" class="mb-06 mt-05 flex items-center gap-05">
				<span class="font-body-m text-ink-60">{{ t('actions.panel.loading') }}</span>

				<Loader size="big" />
			</div>

			<div v-if="allActionsCount" :class="{ 'pointer-events-none opacity-50': isLoading }">
				<div v-for="actionsByStore in sortedActionsByStore" :key="actionsByStore.store" class="mb-05">
					<div
						:class="[
							'portalColor font-body-s-medium col-span-6 mb-03 flex w-full items-center justify-between rounded-t-sm py-01 pl-05 pr-03 text-ink-inverted-87',
							`portal--${actionsByStore.portal}`
						]"
					>
						<div class="flex items-center">
							<PortalIcon :portal="actionsByStore.portal" class="icon--size-1 mr-03" />

							{{ actionsByStore.name }} ({{ actionsByStore.actions.length }})
						</div>

						<IconButton
							icon="chevron-down"
							variant="invert"
							size="xs"
							:class="['expandIcon ml-05 cursor-pointer', { expandIcon__unrotated: isStoreCollapsed.get(actionsByStore.store) }]"
							@click="isStoreCollapsed.set(actionsByStore.store, !isStoreCollapsed.get(actionsByStore.store))"
						/>
					</div>

					<template v-if="!isStoreCollapsed.get(actionsByStore.store)">
						<div v-for="action in actionsByStore.actions" :key="action.hash" class="mb-03 rounded-sm bg-level-01">
							<TimelineEventItem :event="actionToTimelineEvent(action)" readonly />

							<div class="p-04">
								<div class="flex items-center justify-between">
									<div class="flex flex-col">
										<div>
											{{ action.skuName }}
										</div>

										{{ getRangeDescriptionWithoutTime(action.start_date, action.end_date) }}
									</div>

									<div class="flex">
										<Button v-if="action.kind === 'unplanned'" size="xs" @click="performAction(action)">{{
											t('actions.panel.plan')
										}}</Button>
										<Button v-else-if="action.kind === 'unscheduled'" size="xs" @click="performAction(action)">
											<div class="flex items-center">
												{{ t('actions.panel.schedule') }}

												<PortalIcon :portal="actionsByStore.portal" class="icon--size-1_25 ml-03" />
											</div>
										</Button>
									</div>
								</div>
							</div>
						</div>
					</template>
				</div>
			</div>

			<div v-else-if="!isLoading" class="flex items-center">
				<Icon name="check-circle-outline" class="text-message-success" size="2" />

				<div class="ml-03 whitespace-pre-line">
					{{ t('actions.panel.emptyStateMessage') }}
				</div>
			</div>
		</div>

		<template #footer>
			<div class="flex justify-end">
				<Button size="s" variant="secondary" @click="closeAdvisedActions()"> {{ t('actions.panel.close') }}</Button>
			</div>
		</template>
	</SidePanelContent>
</template>

<script setup lang="ts">
import { type StoreName } from '@bluebrick/discounts-service-module'
import { Button, Icon, IconButton, Loader, PortalIcon, useTranslation } from '@bluebrick/saas-ui'
import { type MbscCalendarEventData } from '@mobiscroll/vue'
import { storeToRefs } from 'pinia'
import { ref } from 'vue'
import { getRangeDescriptionWithoutTime, openLink } from '@/common/utils'
import { type ActionWithMetadata } from '@/features/actions/types'
import { useActionsStore } from '@/features/actions/useActionsStore'
import { useEventDetailsStore } from '@/features/eventDetails/useEventDetailsStore'
import TimelineEventItem from '@/features/events/components/timeline/TimelineEventItem.vue'
import { useEventsStore } from '@/features/events/useEventsStore'
import { useNotificationsStore } from '@/features/notifications/useNotificationsStore'
import SidePanelContent from '@/features/sidePanel/components/SidePanelContent.vue'
import { useSidebarStore } from '@/features/sidePanel/useSidebarStore'
import { useStoresStore } from '@/features/stores/useStoresStore'
import { type CustomDimensions } from '@/features/telemetry/types'
import { useTelemetry } from '@/features/telemetry/useTelemetry'

const { t } = useTranslation()
const { closeAdvisedActions } = useSidebarStore()
const { sortedActionsByStore, allActionsCount, isLoading } = storeToRefs(useActionsStore())
const { storeEvents } = storeToRefs(useEventsStore())
const { updateEventOrCreateFromExisting } = useEventDetailsStore()
const { stores } = storeToRefs(useStoresStore())
const { trackException, trackEvent } = useTelemetry()
const { notifyError } = useNotificationsStore()
const { toggleStoreContainerExpandedState } = useStoresStore()

const isStoreCollapsed = ref(new Map<StoreName, boolean>())

function actionToTelemetryPayload(action: ActionWithMetadata): CustomDimensions {
	return {
		storeName: action.store_name,
		startDate: action.start_date.toISO() ?? '',
		endDate: action.end_date.toISO() ?? '',
		eventType: action.event_type
	}
}

function handleError(action: ActionWithMetadata) {
	const message = t('actions.panel.eventNotFound')

	trackException(message, message, actionToTelemetryPayload(action))

	notifyError(message)
}

function performAction(action: ActionWithMetadata) {
	const skuData = storeEvents.value.get(action.store_name)?.skus.find(({ unique_sku_id }) => unique_sku_id === action.unique_sku_id)

	if (!skuData) {
		handleError(action)

		return
	}

	const timelineEvent = skuData.events.find(({ hash }) => hash === action.hash)

	if (!timelineEvent) {
		handleError(action)

		return
	}

	if (action.kind === 'unplanned') {
		const store = stores.value.find(({ id }) => id === action.store_name)

		if (!store) {
			handleError(action)

			return
		}

		trackEvent('user', 'Executing action on unplanned event', actionToTelemetryPayload(action))

		toggleStoreContainerExpandedState(store.id, true)

		updateEventOrCreateFromExisting(store, timelineEvent, skuData.unique_sku_id)
	} else if (action.kind === 'unscheduled') {
		trackEvent('user', 'Executing action on unscheduled event', actionToTelemetryPayload(action))

		if (action.store_name === 'steam') {
			openLink('https://partner.steamgames.com/')
		} else if (action.store_name.startsWith('nintendo')) {
			openLink('https://developer.nintendo.com/group/development/home#')
		}
	}
}

function actionToTimelineEvent(action: ActionWithMetadata): MbscCalendarEventData {
	const now = new Date()

	return { date: 0, startDate: now, endDate: now, original: { rawEvent: action } }
}
</script>

<style lang="scss" scoped>
.advisedActionsPanel {
	.expandIcon {
		transition: all 0.1s linear;

		&__rotated {
			transform: rotate(-90deg);
		}

		&__unrotated {
			transform: rotate(90deg);
		}
	}
}
</style>
